import React, { useEffect, useMemo, useState } from 'react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList } from 'recharts';
import { GlassCard } from './GlassCard';
import { getCheckInHistory } from '../lib/api';

const METRICS = [
  { key: 'mood', label: 'Mood', icon: 'fa-face-smile', color: '#7C3AED', goodHigh: true, help: 'How positive you felt overall' },
  { key: 'energy', label: 'Energy', icon: 'fa-bolt', color: '#F59E0B', goodHigh: true, help: 'Your get-up-and-go through the day' },
  { key: 'sleep_quality', label: 'Sleep', icon: 'fa-moon', color: '#6366F1', goodHigh: true, help: 'How restful your sleep was' },
  { key: 'stress_level', label: 'Stress', icon: 'fa-wind', color: '#14B8A6', goodHigh: false, help: 'Lower is calmer' },
];

const SCALE_LABELS = { 0: 'Low', 1: '', 2: '', 3: 'Mid', 4: '', 5: 'High' };

function fmtDate(d) {
  try {
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch {
    return d;
  }
}

function avg(arr) {
  const v = arr.filter((x) => typeof x === 'number');
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : 0;
}

export function TrendCharts({ userId }) {
  const [rows, setRows] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState('mood');

  useEffect(() => {
    if (!userId) return;
    getCheckInHistory(userId, 30)
      .then((history) => {
        const sorted = (history || []).slice().sort((a, b) => (a.date > b.date ? 1 : -1));
        setRows(
          sorted.map((c) => ({
            date: fmtDate(c.date),
            mood: c.mood,
            energy: c.energy,
            sleep_quality: c.sleep_quality,
            stress_level: c.stress_level,
          }))
        );
        const counts = {};
        (history || []).forEach((c) => (c.symptoms || []).forEach((s) => { counts[s] = (counts[s] || 0) + 1; }));
        setSymptoms(
          Object.entries(counts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 6)
        );
      })
      .finally(() => setLoading(false));
  }, [userId]);

  const stats = useMemo(() => {
    const out = {};
    METRICS.forEach((m) => {
      const series = rows.map((r) => r[m.key]);
      const half = Math.floor(series.length / 2) || 1;
      const older = avg(series.slice(0, half));
      const recent = avg(series.slice(-half));
      const delta = recent - older;
      out[m.key] = { avg: avg(series), recent, delta };
    });
    return out;
  }, [rows]);

  const activeMeta = METRICS.find((m) => m.key === active);
  const activeStat = stats[active] || { avg: 0, delta: 0 };

  const trendText = (() => {
    const { delta } = activeStat;
    const improving = activeMeta.goodHigh ? delta > 0.3 : delta < -0.3;
    const worsening = activeMeta.goodHigh ? delta < -0.3 : delta > 0.3;
    if (Math.abs(delta) <= 0.3) return { label: 'Holding steady', color: '#6B6788', icon: 'fa-arrow-right-long' };
    if (improving) return { label: activeMeta.goodHigh ? 'Trending up' : 'Easing off', color: '#10B981', icon: 'fa-arrow-trend-up' };
    if (worsening) return { label: activeMeta.goodHigh ? 'Dipping' : 'Building up', color: '#EF4444', icon: 'fa-arrow-trend-down' };
    return { label: 'Holding steady', color: '#6B6788', icon: 'fa-arrow-right-long' };
  })();

  if (loading) {
    return <GlassCard className="p-8 text-center text-[var(--aura-ink-soft)]"><i className="fa-solid fa-circle-notch fa-spin" /></GlassCard>;
  }
  if (rows.length === 0) {
    return (
      <GlassCard className="p-8 text-center">
        <div className="grid place-items-center w-14 h-14 rounded-2xl mx-auto mb-3 bg-[var(--aura-purple)]/10 text-[var(--aura-purple)]"><i className="fa-solid fa-chart-line text-xl" /></div>
        <p className="text-[var(--aura-ink-soft)]">Complete a few daily check-ins to see your sleep, mood and symptom trends here.</p>
      </GlassCard>
    );
  }

  return (
    <div className="space-y-4">
      {/* Metric summary cards — click to focus */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="trend-metric-cards">
        {METRICS.map((m) => {
          const s = stats[m.key] || { avg: 0, delta: 0 };
          const isActive = active === m.key;
          const up = s.delta > 0.15;
          const down = s.delta < -0.15;
          return (
            <button
              key={m.key}
              data-testid={`trend-metric-${m.key}`}
              onClick={() => setActive(m.key)}
              className="text-left rounded-2xl p-4 border-2 transition-base"
              style={isActive ? { borderColor: m.color, background: `${m.color}0e` } : { borderColor: 'var(--aura-line)', background: '#fff' }}
            >
              <div className="flex items-center justify-between">
                <span className="grid place-items-center w-9 h-9 rounded-xl" style={{ background: `${m.color}18`, color: m.color }}>
                  <i className={`fa-solid ${m.icon}`} />
                </span>
                {(up || down) ? (
                  <span className="text-xs font-semibold flex items-center gap-1" style={{ color: up ? (m.goodHigh ? '#10B981' : '#EF4444') : (m.goodHigh ? '#EF4444' : '#10B981') }}>
                    <i className={`fa-solid ${up ? 'fa-caret-up' : 'fa-caret-down'}`} />
                  </span>
                ) : (
                  <span className="text-xs text-[var(--aura-ink-soft)]"><i className="fa-solid fa-minus" /></span>
                )}
              </div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-display text-2xl" style={{ color: m.color }}>{s.avg.toFixed(1)}</span>
                <span className="text-xs text-[var(--aura-ink-soft)]">/ 5 avg</span>
              </div>
              <div className="text-sm font-medium">{m.label}</div>
            </button>
          );
        })}
      </div>

      {/* Focused area chart */}
      <GlassCard className="p-5" data-testid="trend-line-chart">
        <div className="flex items-start justify-between mb-1 flex-wrap gap-2">
          <div>
            <h3 className="font-display text-lg flex items-center gap-2">
              <i className={`fa-solid ${activeMeta.icon}`} style={{ color: activeMeta.color }} /> {activeMeta.label} over time
            </h3>
            <p className="text-xs text-[var(--aura-ink-soft)]">{activeMeta.help} · last {rows.length} check-ins</p>
          </div>
          <span className="text-xs font-semibold rounded-full px-3 py-1.5 flex items-center gap-1.5" style={{ color: trendText.color, background: `${trendText.color}14` }}>
            <i className={`fa-solid ${trendText.icon}`} /> {trendText.label}
          </span>
        </div>
        <ResponsiveContainer width="100%" height={230}>
          <AreaChart data={rows} margin={{ top: 10, right: 10, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={activeMeta.color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={activeMeta.color} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="4 4" stroke="rgba(124,58,237,0.08)" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6B6788' }} axisLine={false} tickLine={false} minTickGap={20} />
            <YAxis domain={[0, 5]} ticks={[0, 3, 5]} tickFormatter={(v) => SCALE_LABELS[v] || v} tick={{ fontSize: 11, fill: '#6B6788' }} axisLine={false} tickLine={false} width={52} />
            <Tooltip
              contentStyle={{ borderRadius: 12, border: '1px solid #E7E4F2', fontSize: 12 }}
              formatter={(v) => [`${v} / 5`, activeMeta.label]}
            />
            <Area type="monotone" dataKey={active} stroke={activeMeta.color} strokeWidth={3} fill="url(#areaFill)" dot={{ r: 3, fill: activeMeta.color, strokeWidth: 0 }} activeDot={{ r: 6 }} />
          </AreaChart>
        </ResponsiveContainer>
      </GlassCard>

      {/* Symptoms */}
      <GlassCard className="p-5" data-testid="trend-symptom-chart">
        <h3 className="font-display text-lg mb-1">How often symptoms showed up</h3>
        <p className="text-xs text-[var(--aura-ink-soft)] mb-3">Number of days logged in the last {rows.length} check-ins</p>
        {symptoms.length === 0 ? (
          <div className="h-[200px] grid place-items-center text-sm text-[var(--aura-ink-soft)]">No symptoms logged recently — that&apos;s great.</div>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(160, symptoms.length * 40)}>
            <BarChart data={symptoms} layout="vertical" margin={{ top: 0, right: 34, left: 8, bottom: 0 }}>
              <XAxis type="number" hide domain={[0, 'dataMax + 1']} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#1E1B3A' }} width={100} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: 'rgba(124,58,237,0.06)' }} contentStyle={{ borderRadius: 12, border: '1px solid #E7E4F2', fontSize: 12 }} formatter={(v) => [`${v} day${v === 1 ? '' : 's'}`, 'Logged']} />
              <defs>
                <linearGradient id="barFill" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#C084FC" />
                  <stop offset="100%" stopColor="#7C3AED" />
                </linearGradient>
              </defs>
              <Bar dataKey="value" radius={[0, 8, 8, 0]} fill="url(#barFill)" barSize={20}>
                <LabelList dataKey="value" position="right" style={{ fontSize: 12, fontWeight: 700, fill: '#7C3AED' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </GlassCard>
    </div>
  );
}
